<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-10 overflow-y-auto"
      @close="$emit('close')"
    >
      <div
        class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 transition-opacity bg-gray-500/75"
          />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >&#8203;</span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
          >
            <div>
              <div class="mt-3 text-center sm:mt-5">
                <DialogTitle
                  as="h3"
                  class="text-lg font-medium leading-6 text-gray-900"
                >
                  {{
                    conferenced ? 'Conference In User' : 'Transfer Call To User'
                  }}
                </DialogTitle>
                <div v-if="!transfering">
                  <div class="sm:hidden">
                    <label
                      for="tabs"
                      class="sr-only"
                    >Select a tab</label>
                    <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                    <select
                      id="tabs"
                      name="tabs"
                      class="block w-full py-2 pl-3 pr-10 text-base border-gray-300 rounded-md focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                      @change="selectTab($event.target.value)"
                    >
                      <option
                        v-for="tab in tabs"
                        :key="tab.name"
                        :value="tab.name"
                        :selected="tab.current"
                      >
                        {{ tab?.name }}
                      </option>
                    </select>
                  </div>
                  <div class="hidden sm:block">
                    <div class="border-b border-gray-200">
                      <nav
                        class="flex -mb-px space-x-8"
                        aria-label="Tabs"
                      >
                        <button
                          v-for="tab in tabs"
                          :key="tab.name"
                          :class="[
                            tab.current
                              ? 'border-teal-500 text-teal-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                            'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm',
                          ]"
                          :aria-current="tab.current ? 'page' : undefined"
                          @click="selectTab(tab.name)"
                        >
                          {{ tab?.name }}
                        </button>
                      </nav>
                    </div>
                  </div>
                  <active-users
                    v-if="
                      currentTab &&
                        currentTab.name === 'Active Users' &&
                        !disable
                    "
                    :users="activeUsers"
                    @transfer="transferUser"
                  />
                  <team-transfer
                    v-if="currentTab && currentTab.name === 'Team' && !disable"
                    :users="activeUsers"
                    @transfer="transferUser"
                  />
                  <external
                    v-if="
                      currentTab && currentTab.name === 'External' && !disable
                    "
                    :external-numbers="externalNumbers"
                    @transfer="transferExternal"
                  />
                  <Queue
                    v-if="currentTab && currentTab.name === 'Queue' && !disable"
                    @transfer="transferQueue"
                  />
                </div>
                <div v-else>
                  Waiting for transfer
                </div>
              </div>
            </div>
            <!-- <div class="mt-5 sm:mt-6">
              <button
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-real-500 sm:text-sm"
                @click="$emit('close')"
              >
                Resume
              </button>
            </div> -->
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  ref,
  defineComponent,
  Ref,
  onMounted,
  onUnmounted,
  computed,
} from 'vue'
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import useLetsDialSocket from '/@/services/letsDialSocket/useLetsDialSocket'
import { User } from '@letsdial/api/src/lib/types/User'
import useLetsDialAPI from '/@/services/letsDialAPI/useLetsDialAPI'
import { useAuthStore } from '/@/store/authStore'
import ActiveUsers from './transferModal/ActiveUsers.vue'
import { IncomingTransferEvent, TransferAcceptEvent } from '@letsdial/socket'
import useLetsDialPhone from '/@/services/letsDialPhone/useLetsDialPhone'
import { useCallStore } from '/@/store/callStore'
import { useRecordStore } from '/@/store/recordStore'
import { Campaign } from '@letsdial/api/src/lib/types/Campaign'
import { Organization } from '@letsdial/api/src/lib/types/Organization'
import { CallType } from '@letsdial/api/src'
import TeamTransfer from './transferModal/TeamTransfer.vue'
import External from './transferModal/External.vue'
import Queue from './transferModal/Queue.vue'
import { useNotificationStore } from '/@/store/notificationStore'
// import { useToast } from 'vue-toastification'
// const toast = useToast()
const props = withDefaults(
  defineProps<{ open: boolean; conferenced: boolean }>(),
  {
    open: () => false,
    conferenced: () => false,
  },
)
const emit = defineEmits<{ close: [] }>()
const notificationStore = useNotificationStore()
const disable = ref(false)
// const open = ref(true)
const transfering = ref(false)
const recordStore = useRecordStore()
const campaign: Ref<Campaign | undefined> = ref(undefined)
const organization: Ref<Organization | undefined> = ref(undefined)
const currentTransfer: Ref<IncomingTransferEvent | undefined> = ref(undefined)
// return {
//   open,
// }
const api = useLetsDialAPI()
const authStore = useAuthStore()
const callStore = useCallStore()
const { phone } = useLetsDialPhone()
const activeUsers: Ref<Array<User>> = ref([])
const loadUsers = async () => {
  if (authStore.selectedOrganization?.id) {
    const response = await api.users.list({
      fields: { users: 'username,myTeams,team' },
      filter: {
        status: 'IDLE',
        organizations: {
          id: authStore.selectedOrganization?.id,
        },
      },
    })
    activeUsers.value = response.data ?? []
  }
}
let interval: any
const tabs: Ref<Array<{ name: string; current: boolean }>> = ref([])
const manualExternalNumbers: Ref<Array<{ name: string; number: string }>> = ref(
  [],
)
const externalNumbers = computed(() => {
  if (campaign.value) {
    return campaign.value.attributes.additionalConfig?.externalTransferNumbers ?? []
  }
  return manualExternalNumbers.value
})
const setTabs = () => {
  const previousActiveTab = tabs.value.find((tab) => tab.current)?.name
  tabs.value = []
  console.log('Campaign Value', campaign.value)
  if (!campaign.value) {
    if (organization.value) {
      const config = organization.value.attributes.additionalConfig
      if (config?.allowTransferToOtherUsers) {
        tabs.value.push({ name: 'Active Users', current: false })
      }
      if (config?.allowTransferToTeamLeader || config?.allowTransferToTeamMembers) {
        tabs.value.push({ name: 'Team', current: false })
      }
      if (manualExternalNumbers.value.length > 0) {
        tabs.value.push({ name: 'External', current: false })
      }
      if (config?.allowTransferToQueues) {
        tabs.value.push({ name: 'Queue', current: false })
      }
    } else {
      tabs.value.push({ name: 'Active Users', current: false })
      tabs.value.push({ name: 'Team', current: false })
      if (manualExternalNumbers.value.length > 0) {
        tabs.value.push({ name: 'External', current: false })
      }
      tabs.value.push({ name: 'Queue', current: false })
    }
  } else {
    if (
      campaign.value.attributes.additionalConfig?.allowTransferToOtherUsers &&
      !tabs.value.some((tab) => tab.name === 'Active Users')
    ) {
      tabs.value.push({
        name: 'Active Users',
        current: false,
      })
    }
    if (
      (campaign.value.attributes.allowTransferToTeamLeader ||
        campaign.value.attributes.allowTransferToTeamMembers) &&
      !tabs.value.some((tab) => tab.name === 'Team')
    ) {
      tabs.value.push({ name: 'Team', current: false })
    }
    const hasExternalNumbers =
      (campaign.value.attributes.additionalConfig?.externalTransferNumbers ?? []).length > 0
    if (
      hasExternalNumbers &&
      !tabs.value.some((tab) => tab.name === 'External')
    ) {
      tabs.value.push({
        name: 'External',
        current: false,
      })
    }
    if (
      campaign.value.attributes.additionalConfig?.allowTransferToQueues &&
      !tabs.value.some((tab) => tab.name === 'Queue')
    ) {
      tabs.value.push({
        name: 'Queue',
        current: false,
      })
    }
  }

  // Set the current tab
  if (tabs.value.length > 0) {
    const tabToActive = tabs.value.find((tab) => tab.name === previousActiveTab)
    if (tabToActive) {
      tabToActive.current = true
    } else {
      tabs.value[0].current = true
    }
  }
}
onMounted(async () => {
  console.log('recordStore')
  console.log(recordStore, callStore)
  setTabs()
  if (recordStore.pop?.campaignID) {
    campaign.value =
      (await api.campaigns.get(recordStore.pop.campaignID)).data ?? undefined
    setTabs()
  }
  if (callStore.call?.relationships?.campaign?.data?.id) {
    campaign.value =
      (
        await api.campaigns.get(
          callStore.call?.relationships?.campaign?.data?.id,
        )
      ).data ?? undefined
    setTabs()
  }

  if (authStore.selectedOrganization?.id && !campaign.value) {
    try {
      const numbers: Array<{ name: string; number: string }> = []
      
      // Load organization-wide external transfer numbers
      const orgResponse = await api.organizations.get(authStore.selectedOrganization.id)
      organization.value = orgResponse.data ?? undefined
      const orgNums = orgResponse.data?.attributes.additionalConfig?.externalTransferNumbers
      if (orgNums && Array.isArray(orgNums)) {
        orgNums.forEach((n) => {
          if (n.name && n.number && !numbers.some((x) => x.number === n.number)) {
            numbers.push({ name: n.name, number: n.number })
          }
        })
      }

      // Load campaign-specific external numbers
      const response = await api.campaigns.list({
        filter: {
          organization: {
            id: authStore.selectedOrganization.id,
          } as any,
        },
      })
      response.data?.forEach((camp) => {
        const campNums =
          camp.attributes.additionalConfig?.externalTransferNumbers
        if (campNums && Array.isArray(campNums)) {
          campNums.forEach((n) => {
            if (n.number && n.name && !numbers.some((x) => x.number === n.number)) {
              numbers.push({ name: n.name, number: n.number })
            }
          })
        }
      })
      
      manualExternalNumbers.value = numbers
      setTabs()
    } catch (e) {
      console.error(e)
    }
  }

  loadUsers()
  interval = setInterval(() => {
    loadUsers()
  }, 5000)
})
onUnmounted(() => {
  clearInterval(interval)
})
const { socket, listen } = useLetsDialSocket()
listen('transfer:accept', async (data: TransferAcceptEvent) => {
  const call = { ...callStore.call }
  if (call?.id) {
    if (!data.accepted) {
      transfering.value = false
    } else {
      if (props.conferenced) {
        callStore.setConferenced(true)
        const transferUser = currentTransfer.value?.userID ?? data.fromID
        await api.transferCallLogs.post({
          type: 'transferCallLogs',
          attributes: {
            conferenced: true,
          },
          relationships: {
            call: { data: { type: 'calls', id: call.id } },
            toUser: {
              data: transferUser
                ? {
                    type: 'users',
                    id: transferUser,
                  }
                : null,
            },
            user: {
              data: authStore.user?.id
                ? { type: 'users', id: authStore.user?.id }
                : null,
            },
          },
        })
        if (!callStore.conferenceRoomId) {
          await phone.referToConference(call.id)
          const conferenceCall = await api.calls.get(call.id)
          phone.joinConference(
            call.id,
            conferenceCall.data?.attributes.asteriskServerIp,
          )
        }
      } else {
        console.log(
          data.userDeviceID
            ? `${currentTransfer.value?.userID + 10000}_${data.userDeviceID}`
            : (currentTransfer.value?.userID ?? data.fromID ?? 0) + 10000,
          currentTransfer.value?.type,
          currentTransfer.value?.callID,
          currentTransfer.value?.campaignID,
          currentTransfer.value?.number,
          currentTransfer.value?.recordID,
        )
        const transferUser = currentTransfer.value?.userID ?? data.fromID
        await api.transferCallLogs.post({
          type: 'transferCallLogs',
          attributes: {
            conferenced: false,
          },
          relationships: {
            call: { data: { type: 'calls', id: call.id } },
            toUser: {
              data: transferUser
                ? {
                    type: 'users',
                    id: transferUser,
                  }
                : null,
            },
            user: {
              data: authStore.user?.id
                ? { type: 'users', id: authStore.user?.id }
                : null,
            },
          },
        })
        phone.referToExtension(
          // @ts-expect-error: Intentional ignore
          data.userDeviceID
            ? `${currentTransfer.value?.userID + 10000}_${data.userDeviceID}`
            : (currentTransfer.value?.userID ?? data.fromID ?? 0) + 10000,
          currentTransfer.value?.type,
          currentTransfer.value?.callID,
          currentTransfer.value?.campaignID,
          currentTransfer.value?.number,
          currentTransfer.value?.recordID,
        )
      }
      // toast("Transfer Successful", {
      //   toastClassName: "my-custom-toast-class",
      //   bodyClassName: ["custom-toast-class-1", "custom-toast-class-2"]
      // })
    }
  }
  transfering.value = false
  currentTransfer.value = undefined
  emit('close')
})

const transferUser = (user: User) => {
  if (
    user.id &&
    authStore.user?.id &&
    authStore.user?.attributes.username &&
    callStore.call?.id &&
    callStore.call?.attributes.number
  ) {
    transfering.value = true
    setTimeout(() => (transfering.value = false), 12000)
    currentTransfer.value = {
      userID: user.id,
      fromID: authStore.user?.id,
      // @ts-expect-error: Intentional ignore
      username: authStore.user.attributes.username,
      campaignID: callStore.call?.relationships?.campaign?.data?.id,
      recordID: callStore.call?.relationships?.listRecord?.data?.id,
      callID: callStore.call?.id,
      asteriskServerIp: callStore.call?.attributes?.asteriskServerIp,
      type: 'calls',
      number: callStore.call?.attributes.number,
      conference: props.conferenced,
    }

    socket.emit('user:request:transfer', currentTransfer.value)
  }
}

const transferQueue = async (queue: Queue) => {
  const queueID = queue.id;
  
  const inboundFlowSteps = queue.attributes?.flows?.inboundFlow?.steps ?? [];
  const hasFlow = inboundFlowSteps.length > 1;

  currentTransfer.value = {
    queueID,
    fromID: authStore.user?.id,
    username: authStore.user?.attributes.username,
    campaignID: callStore.call?.relationships?.campaign?.data?.id,
    recordID: callStore.call?.relationships?.listRecord?.data?.id,
    asteriskServerIp: callStore.call?.attributes?.asteriskServerIp,
    callID: callStore.call?.id,
    type: 'calls',
    number: callStore.call?.attributes.number,
    conference: props.conferenced,
  }

  if (!props.conferenced && hasFlow && callStore.call?.id) {
    transfering.value = true;
    try {
      await phone.referToQueue(queueID, callStore.call.id);
    } catch (e) {
      console.error('Failed to refer to queue flow', e);
    }
    transfering.value = false;
    currentTransfer.value = undefined;
    emit('close');
     notificationStore.setNotification(
          'Failed to refer to queue',
          `Failed to refer to queue ${queue.attributes.name}`,
          'Error',
          undefined,
          5000,
        )
  } else {
    socket.emit('user:request:queue:transfer', currentTransfer.value)
  }
}

const transferExternal = async (external: { number: string; name: string }) => {
  if (disable.value) {
    return
  }
  disable.value = true
  try {
    const call = { ...callStore.call }
    if (call?.id) {
      if (props.conferenced) {
        const call = await api.calls.post({
          type: 'calls',
          attributes: {
            number: external.number,
            callType: CallType.conferenceCalls,
            additionalData: {
              roomCode: callStore.call?.id ?? authStore.user?.id,
            },
          },
          relationships: {
            user: {
              data: authStore.user?.id
                ? { type: 'users', id: authStore.user?.id }
                : null,
            },
            parent: {
              data: callStore.call?.id
                ? { type: 'calls', id: callStore.call.id }
                : null,
            },
            organization: {
              data: authStore.selectedOrganization?.id
                ? {
                    type: 'organizations',
                    id: authStore.selectedOrganization?.id,
                  }
                : null,
            },
          },
        })
        if (call.data && authStore.user?.id) {
          if (!callStore.conferenceRoomId) {
            await api.transferCallLogs.post({
              type: 'transferCallLogs',
              attributes: {
                conferenced: true,
                toNumber: external.number,
              },
              relationships: {
                call: {
                  data: callStore.call?.id
                    ? { type: 'calls', id: callStore.call.id }
                    : null,
                },
                conferencedCall: {
                  data: call.data.id
                    ? { type: 'calls', id: call.data.id }
                    : null,
                },
                user: {
                  data: authStore.user?.id
                    ? { type: 'users', id: authStore.user?.id }
                    : null,
                },
              },
            })
            await phone.referToConference(
              callStore.call?.id ?? authStore.user.id,
            )
            const conferenceCall = await api.calls.get(callStore.call?.id)
            phone.joinConference(
              callStore.call?.id ?? authStore.user.id,
              conferenceCall.data?.attributes.asteriskServerIp,
            )
          }
        }
      } else {
        await api.transferCallLogs.post({
          type: 'transferCallLogs',
          attributes: {
            conferenced: false,
            toNumber: external.number,
          },
          relationships: {
            call: { data: { type: 'calls', id: call.id } },
            user: {
              data: authStore.user?.id
                ? { type: 'users', id: authStore.user?.id }
                : null,
            },
          },
        })
        phone.referExternal(external.number)
      }
    }
  } catch {}
  transfering.value = false
  currentTransfer.value = undefined
  disable.value = false
  emit('close')
}
const currentTab = computed(() => {
  return tabs.value.find((tab) => tab.current)
})
const selectTab = (name) => {
  tabs.value.forEach((tab) => {
    tab.current = false
    if (tab.name === name) {
      tab.current = true
    }
  })
}
</script>
